import {PropsWithChildren, ReactNode, useId} from "react";

export type OtsResourceCardProps = PropsWithChildren<{
  icon?: ReactNode;
  /** A short label above the title, e.g. "Documentation". */
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Links or actions along the bottom, pinned there when cards in a row differ in height. */
  footer?: ReactNode;
  /** 2 for cards that are sections of the page; 3 when they sit under a section heading of their own. */
  headingLevel?: 2 | 3;
  className?: string;
}>;

/** A card for somewhere to go or something to use: an icon, what it is, and how to get to it. */
export const OtsResourceCard = ({
  icon,
  eyebrow,
  title,
  description,
  footer,
  headingLevel = 2,
  className,
  children
}: OtsResourceCardProps) => {
  const headingId = useId();
  const Heading = headingLevel === 3 ? "h3" : "h2";

  return (
    <section
      aria-labelledby={headingId}
      className={["flex flex-col rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] p-5 sm:p-6", className].filter(Boolean).join(" ")}>
      <div className="flex items-start gap-4">
        {icon && (
          <span aria-hidden="true" className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--ots-primary-container)] text-[var(--ots-primary)] [&>svg]:size-5">
            {icon}
          </span>
        )}
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--ots-text-muted)]">{eyebrow}</p>
          )}
          <Heading
            id={headingId}
            className={[
              eyebrow ? "mt-1" : "",
              "font-[family-name:var(--ots-font-display)] text-base font-semibold text-[var(--ots-secondary)]"
            ].join(" ")}>
            {title}
          </Heading>
          {description && <p className="mt-1.5 text-sm leading-6 text-[var(--ots-text-muted)]">{description}</p>}
        </div>
      </div>
      {children && <div className="mt-5">{children}</div>}
      {footer && <div className="mt-auto flex flex-wrap items-center gap-x-5 gap-y-3 pt-5">{footer}</div>}
    </section>
  );
};
